import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { useAuth } from 'contexts/auth';
import { AUTH_TOKEN_CHANGED_EVENT, refreshToken } from 'services/auth';
import { REFRESH_LEAD_MS, isTokenExpired, msUntilExpiry } from 'services/jwt';

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

export const SessionWatcher = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const goUnauthorized = () => {
      logout();
      navigate('/unauthorized', {
        state: { message: new Error(SESSION_EXPIRED_MESSAGE) },
        replace: true
      });
    };

    const triggerRefresh = async () => {
      if (cancelled) return;
      const fresh = await refreshToken();
      if (cancelled) return;
      if (!fresh) goUnauthorized();
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const current = localStorage.getItem('token');
      if (!current) {
        goUnauthorized();
        return;
      }
      if (isTokenExpired(current)) {
        void triggerRefresh();
        return;
      }
      const ms = msUntilExpiry(current);
      if (ms !== null && ms <= REFRESH_LEAD_MS) void triggerRefresh();
    };

    const onTokenChanged = () => setVersion(v => v + 1);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);

    const token = localStorage.getItem('token');
    const remaining = msUntilExpiry(token);

    if (!token || remaining === null) {
      goUnauthorized();
    } else if (isTokenExpired(token)) {
      void triggerRefresh();
    } else {
      timer = window.setTimeout(triggerRefresh, Math.max(0, remaining - REFRESH_LEAD_MS));
    }

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);
    };
  }, [version, navigate, logout]);

  return <Outlet />;
};

export default SessionWatcher;
